import { useLayoutEffect, useRef, useState } from "react";

/**
 * Which of the Facts tab's three layouts fits the room the tab ACTUALLY has.
 *
 * | Layout    | List                  | Correction pane              |
 * |-----------|-----------------------|------------------------------|
 * | `split`   | the table             | a column beside it           |
 * | `stacked` | the table, full width | a sheet from the right        |
 * | `narrow`  | stacked two-line rows | a bottom sheet over the list |
 *
 * WHY THE TAB'S OWN WIDTH, NOT THE VIEWPORT'S (Damien, 2026-09-13, AreaRail
 * Option 1: "side by side only on wider screens, stacked below that — no
 * horizontal scroll at any width with the rail present"). The shell draws the
 * AreaRail at 360px beside every module, and the viewer can collapse it, so a
 * viewport breakpoint cannot know how wide the tab is: at 1440 the old `lg`
 * split left the list 395px for an ~840px table, which scrolled sideways on
 * every production screen. Measuring the tab answers the question the layout
 * is really asking — does the table fit? — whether the rail is open or not.
 *
 * The thresholds are the widest list this tab draws, measured, not guessed:
 * the all-products table (Fact / Product / Market / Value / Status / Last
 * checked) at the brand's 20px rem is ~970px at its narrowest. The pane track
 * is 28.75rem (575px) plus the 24px gap and the pane's 24px inset. One pair of
 * thresholds for both views, so switching to "all products" never moves the
 * pane from beside the list to under it.
 */
export type FactsLayout = "narrow" | "stacked" | "split";

/** Below this the table does not fit even at full width. */
export const STACKED_MIN_PX = 1000;
/** The widest table beside the pane: 1000 + 575 + 24 + 24. */
export const SPLIT_MIN_PX = 1623;

/**
 * Below this the three filter selects no longer fit on one line, so they fold
 * behind one "Filters" button. Separate from `narrow`: a 1280px laptop with the
 * rail open is too narrow for the table but has plenty of room for three
 * selects, and hiding them there would cost a click for nothing.
 */
export const FILTERS_FOLD_PX = 640;

export function layoutFor(width: number): FactsLayout {
  if (width >= SPLIT_MIN_PX) return "split";
  if (width >= STACKED_MIN_PX) return "stacked";
  return "narrow";
}

/**
 * Measures the element the ref lands on and tracks it as it resizes.
 *
 * `useLayoutEffect`, so the first measurement lands before the first paint — a
 * phone never sees the table flash before the stacked rows replace it. Until
 * there is an element to measure (a server render, a test renderer) it reports
 * `stacked`: the pane renders inline, which degrades to a longer page rather
 * than to a fact that cannot be opened at all.
 */
export function useFactsLayout<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  // The DERIVED answer is the state, not the width: a window dragged across a
  // hundred pixels would otherwise re-render the whole tab a hundred times to
  // arrive at the same layout.
  const [state, setState] = useState<{ layout: FactsLayout; filtersFolded: boolean } | null>(
    null,
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sync = () => {
      const width = el.getBoundingClientRect().width;
      const layout = layoutFor(width);
      const filtersFolded = width < FILTERS_FOLD_PX;
      setState((prev) =>
        prev?.layout === layout && prev.filtersFolded === filtersFolded
          ? prev
          : { layout, filtersFolded },
      );
    };
    sync();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return {
    ref,
    layout: state?.layout ?? ("stacked" as const),
    filtersFolded: state?.filtersFolded ?? false,
  };
}
